import { environment } from '@env/environment';
import { Building } from './building.model';
import { BaseModel } from './base.model';
import { Image } from './image.model';
import { Contract } from './contract.model';

export class UnitFilters {
  search: string | null = null;
  buildingIds: number[] = [];
  statuses: string[] = [];
  floors: number[] = [];
  types: string[] = [];
  subtypes: string[] = [];
  published: boolean | null = null;
  extra: string[] = [];

  constructor(data?: Partial<UnitFilters>) {
    if (data) Object.assign(this, data);
  }

  getFilterQuery(): Record<string, any> {
    const query: Record<string, any> = {};
    if (this.search) query['search'] = this.search;
    if (this.buildingIds.length) query['building_ids[]'] = this.buildingIds;
    if (this.statuses.length) query['statuses[]'] = this.statuses;
    if (this.floors.length) query['floors[]'] = this.floors;
    if (this.types.length) query['types[]'] = this.types;
    if (this.subtypes.length) query['subtypes[]'] = this.subtypes;
    if (this.extra.includes('published')) query['published'] = true;
    if (this.extra.includes('has_applications')) query['has_applications'] = true;

    return query;
  }

  get hasActiveFilters(): boolean {
    return !!(this.search || this.buildingIds.length || this.statuses.length || this.floors.length);
  }

  reset(): void {
    this.search = null;
    this.buildingIds = [];
    this.statuses = [];
    this.floors = [];
    this.published = null;
    this.extra = [];
  }
}

export class Unit extends BaseModel {
  static TYPE_RESIDENTIAL = 'residential';
  static TYPE_BUSINESS = 'business';
  static TYPE_PARKING = 'parking';
  static TYPE_STORAGE = 'storage';

  static SUBTYPE_LABOR = 'labor';
  static SUBTYPE_OFFICE = 'office';
  static SUBTYPE_SMALL_OFFICE = 'small_office';
  static SUBTYPE_WORKSHOP = 'workshop';
  static SUBTYPE_WAREHOUSE = 'warehouse';
  static SUBTYPE_KITCHEN = 'kitchen';
  static SUBTYPE_GASTRONOMY = 'gastronomy';

  static SUBTYPE_APARTMENT = 'apartment';
  static SUBTYPE_HOUSE = 'house';

  static SUBTYPE_UNDERGROUND_PARKING = 'underground_parking';
  static SUBTYPE_OUTDOOR_PARKING = 'outdoor_parking';
  static SUBTYPE_EV_CHARGING = 'ev_charging';
  static SUBTYPE_DUPLEX_PARKING = 'duplex_parking';
  static SUBTYPE_SINGLE_GARAGE = 'single_garage';
  static SUBTYPE_MOTORCYCLE_PARKING = 'motorcycle_parking';
  static SUBTYPE_BICYCLE_PARKING = 'bicycle_parking';

  static SUBTYPE_STORAGE_ROOM = 'storage_room';
  static SUBTYPE_CELLAR = 'cellar';
  static SUBTYPE_ATTIC = 'attic';
  static SUBTYPE_UTILITY_ROOM = 'utility_room';
  static SUBTYPE_CRAFT_ROOM = 'craft_room';
  static SUBTYPE_HOBBY_ROOM = 'hobby_room';
  static SUBTYPE_WORKROOM = 'workroom';
  static SUBTYPE_ATELIER = 'atelier';
  static SUBTYPE_MUSIC_ROOM = 'music_room';

  static DESIGNATION_SELL = 'sell';
  static DESIGNATION_RENT = 'rent';

  static STATUS_AVAILABLE = 'available';
  static STATUS_UNAVAILABLE = 'unavailable';
  static STATUS_RESERVED = 'reserved';
  static STATUS_RENTED = 'rented';

  static FITOUT_FINISHED = 'finished';
  static FITOUT_SHELL = 'shell';

  static allTypes(): string[] {
    return [
      Unit.TYPE_RESIDENTIAL,
      Unit.TYPE_BUSINESS,
      Unit.TYPE_PARKING,
      Unit.TYPE_STORAGE,
    ];
  }

  id!: number;
  instanceId?: number;
  buildingId!: number;
  propertyId!: string;

  coverImageId?: number;
  floorplanId?: number;
  floorplan?: {
    id: number;
    originalName?: string;
    mimeType?: string;
    url?: string;
  };

  name!: string;
  description!: string;
  address!: string;
  type!: string;
  subtype!: string;
  designation!: string;
  fitoutStandard?: string;
  roomNum!: string;
  numWorkstations?: number;
  floor!: number;
  totalSquareArea?: number;
  basementArea?: number;
  outdoorArea?: number;
  price?: string;
  priceNote?: string;
  minRentMonths?: number;
  auxiliaryCost?: number;
  status!: string;
  publicStatus?: string;
  published!: boolean;
  tour3dLink?: string;
  configuratorLink?: string;
  applicationLink?: string;
  moveIn?: string;
  ownMoveIn?: string | null;
  perspectiveIndex?: number;

  activeContractId?: number;
  externalSource?: string | null;
  externalId?: string | null;
  syncedFields?: string[];

  createdBy?: number;
  updatedBy?: number;
  createdAt?: string;
  updatedAt?: string;

  images?: Image[];
  coverImage?: Image;
  building?: Building;
  leadsCount!: number;
  alternativeLeadsCount?: number;
  activeContract?: Contract;

  get childModels() {
    return {
      building: Building,
      images: Image,
      coverImage: Image,
      activeContract: Contract,
    };
  }

  get isManagedBySync(): boolean {
    return !!this.syncedFields?.length;
  }

  isFieldSynced(field: string): boolean {
    return !!this.syncedFields?.includes(field);
  }

  get coverImageUrl(): string | null {
    if (!this.coverImageId) {
      return null;
    }

    return `${environment.apiBaseUrl}/${this.instanceId}/images/${this.coverImageId}`;
  }

  get floorplanUrl(): string | null {
    if (!this.floorplanId) {
      return null;
    }

    return `${environment.apiBaseUrl}/${this.instanceId}/files/${this.floorplanId}`;
  }

  get floorplanFileName(): string | null {
    return this.floorplan?.originalName ?? null;
  }

  get totalGrossPrice(): number | null {

    if (isNaN(Number(this.price))) {
      return null;
    }

    return Number(this.price ?? 0) + Number(this.auxiliaryCost ?? this.building?.auxiliaryCost ?? 0);
  }
}
